import {createSlice} from '@reduxjs/toolkit'
import StatuspageService from '../../api/statuspage'
import {toast} from 'react-toastify'
import FormHelper, {hasIntersection} from '../../utils/forms'
import {routes} from '../../routes'
import data from '../../views/statuspages/data.json'
import {doNothing} from '../../utils/placeholder'
import statusCodes from '../../utils/http'

const defaultSelectedTab = 'settings'

export const statuspageSlice = createSlice({
    name: 'statuspage',
    initialState: {
        statuspages: [],
        isProcessing: false,
        isDeleting: false,
        statuspageData: {},
        cnameData: {},
        selectedTab: defaultSelectedTab
    },
    reducers: {
        setStatuspages: (state, {payload}) => {
            state.statuspages = payload
        },
        setIsProcessing: (state, {payload}) => {
            state.isProcessing = payload
        },
        setIsDeleting: (state, {payload}) => {
            state.isDeleting = payload
        },
        setStatuspageData: (state, {payload}) => {
            state.statuspageData = payload
        },
        setCnameData: (state, {payload}) => {
            state.cnameData = payload
        },
        setSelectedTab: (state, {payload}) => {
            state.selectedTab = payload
        }
    }
})

export const {
    setStatuspages,
    setIsProcessing,
    setStatuspageData,
    setCnameData,
    setIsDeleting,
    setSelectedTab
} = statuspageSlice.actions

export default statuspageSlice.reducer

const componentTypeCustom = 'custom'

export const createStatuspage = (formData, navigate, ...errors) => {
    return async(dispatch) => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await StatuspageService.createStatuspage(formData)
            toast.success(data.message)
            navigate(routes.statuspages.path)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const fieldsArr = Object.keys(fields)
            const message = res?.message

            if (hasIntersection(fieldsArr, data?.settingsFormFields)) {
                FormHelper.setFormElementError(fields, message, ...errors)
            }
        }
        dispatch(setIsProcessing(false))
    }
}

export const getDomainCname = (domain) => {
    return async(dispatch) => {
        try {
            const {data} = await StatuspageService.getDomainCname(domain)
            dispatch(setCnameData(data?.data?.cname))
        } catch (e) {
            dispatch(
                setCnameData({
                    configured: false,
                    value: null
                })
            )
        }
    }
}

export const updateStatuspage = (id, formData, ...errors) => {
    return async dispatch => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await StatuspageService.updateStatuspage(id, formData)
            toast.success(data.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const fieldsArr = Object.keys(fields)
            const message = res?.message

            if (hasIntersection(fieldsArr, data?.settingsFormFields)) {
                FormHelper.setFormElementError(fields, message, ...errors)
            }
        }
        dispatch(setIsProcessing(false))
    }
}

export const deleteStatuspage = (id) => {
    return async(dispatch) => {
        dispatch(setIsDeleting(true))
        try {
            const {data} = await StatuspageService.deleteStatuspage(id)
            toast.success(data.message)
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
        dispatch(setIsDeleting(false))
    }
}

export const getStatuspages = (showLoader = true) => {
    return async(dispatch) => {
        showLoader && dispatch(setIsProcessing(true))
        try {
            const {data} = await StatuspageService.getStatuspages()
            dispatch(setStatuspages(data?.data?.statuspages || []))
        } catch (e) {
            doNothing()
        }
        dispatch(setIsProcessing(false))
    }
}

export const getStatuspage = (id, navigate) => {
    return async(dispatch) => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await StatuspageService.getStatuspage(id)
            dispatch(setStatuspageData(data?.data))
        } catch (e) {
            const res = e?.response
            if (statusCodes.SpecialErrors.includes(res?.status)) {
                navigate(routes.not_found.path)
            }
        }
        dispatch(setIsProcessing(false))
    }
}

export const toggleStatuspageStatus = (id, status) => {
    return async(dispatch) => {
        try {
            const {data} = await StatuspageService.changeStatus(id, status)
            dispatch(setStatuspageData(data?.data))
            toast.success(data?.message)
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
    }
}

export const getStatuspagesComponents = (statuspages) => {
    return async(dispatch) => {
        dispatch(setIsProcessing(true))
        try {
            const promises = statuspages?.map((statuspage) => {
                return StatuspageService.getStatuspageComponents(statuspage?.id)
            })
            const res = await Promise.all(promises)
            return res?.map((item, index) => {
                return {
                    components: item?.data?.data?.components
                        ?.filter((item) => item?.type === componentTypeCustom)
                        ?.map((item) => {
                            return {
                                label: item?.name,
                                value: item?.id
                            }
                        }),
                    statuspage_id: statuspages?.[index]?.id
                }
            })
        } catch (e) {
            doNothing()
        }
        dispatch(setIsProcessing(false))
    }
}
